import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthStore } from '../../stores/auth.store';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';
import { AppStorageService } from '../services/app-storage.service';
import { sanitizeGuardReturnUrl } from './role.guard';
import { sanitizeUrl } from '@core/utils/url-sanitizer';
import { firstValueFrom, race, of, timer } from 'rxjs';
import { filter, map, take, catchError } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * Authentication guard to protect routes that require user authentication.
 *
 * MEDIUM-3 FIX (DEV-1436): Replaced polling loop with observable-based approach.
 * This is more efficient and follows Angular/RxJS best practices.
 *
 * This guard handles the timing issue during page reload where:
 * 1. User reloads page with active session in localStorage
 * 2. APP_INITIALIZER starts token refresh
 * 3. Guard runs (potentially before refresh completes)
 *
 * Solution: Use observables to reactively wait for auth state changes
 * instead of polling with setTimeout loops.
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService);
  const appStorage = inject(AppStorageService);

  // Check if already authenticated
  if (authService.isAuthenticated() || authStore.isLoggedIn()) {
    // Sync store if needed
    if (authService.isAuthenticated() && !authStore.isLoggedIn()) {
      authStore.syncWithAuthService();
    }
    return true;
  }

  // Check if there's a stored session (indicating user was logged in before reload)
  const hasStoredSession = appStorage.getItem('razor_session_active') === 'true';
  const hasStoredUser = !!appStorage.getItem('razor_user');
  // HIGH-1 FIX: No longer check for refresh token in localStorage
  // Refresh token is now only in HttpOnly cookie

  // If there's evidence of a previous session, wait for token refresh to complete
  if (hasStoredSession && hasStoredUser) {
    // MEDIUM-3 FIX: Use observable-based waiting instead of polling loop
    // Reduced from 3000ms to 1500ms - backend responds in <100ms,
    // so 1500ms accounts for network latency with generous margin.
    const MAX_WAIT_MS = 1500;

    try {
      // Create observable from the isAuthenticated signal
      const isAuth$ = toObservable(authService.isAuthenticated);

      // Wait for auth to become true OR timeout
      const authResult = await firstValueFrom(
        race([
          // Wait for authentication to become true
          isAuth$.pipe(
            filter(isAuth => isAuth === true),
            take(1),
            map(() => true)
          ),
          // Or timeout after MAX_WAIT_MS
          timer(MAX_WAIT_MS).pipe(
            map(() =>
              // Check one final time after timeout
               authService.isAuthenticated()
            )
          )
        ]).pipe(
          // Handle any errors gracefully
          catchError(err => {
            logger.warn('[authGuard] Auth race failed, falling back to direct check', { error: err });
            return of(authService.isAuthenticated());
          })
        )
      );

      if (authResult) {
        authStore.syncWithAuthService();
        return true;
      }

      // Auth failed after timeout - clean up stale localStorage
      // to prevent repeated waits on subsequent navigation attempts.
      // Only clear if auth service confirms no valid token exists,
      // avoiding a race condition where the token refresh HTTP response
      // arrives just after the timer fires but before we clear.
      if (!authService.isAuthenticated() && !authService.getToken()) {
        appStorage.removeItem('razor_session_active');
        appStorage.removeItem('razor_user');
        logger.warn('[authGuard] Cleared stale session indicators after timeout', { url: sanitizeUrl(state.url) });
      }
    } catch {
      // If observable approach fails, fall back to direct check
      if (authService.isAuthenticated()) {
        authStore.syncWithAuthService();
        return true;
      }

      // Clean up stale localStorage on error path too (same race-safe check)
      if (!authService.getToken()) {
        appStorage.removeItem('razor_session_active');
        appStorage.removeItem('razor_user');
        logger.warn('[authGuard] Cleared stale session indicators after error', { url: sanitizeUrl(state.url) });
      }
    }
  }

  // No valid session - redirect to login
  // GAP-FE-C013: session-scoped return URL (consumed once after login, not
  // persisted across sessions). Routed through AppStorageService's session
  // methods (sessionStorage-backed) so it is SSR-safe and mockable — no longer
  // raw sessionStorage. Session scope is preserved (setSessionItem).
  appStorage.setSessionItem('auth_return_url', sanitizeGuardReturnUrl(state.url));
  router.navigate(['/auth/login']);
  return false;
};

/**
 * Guest guard to prevent authenticated users from accessing auth pages
 * Checks BOTH AuthStore and AuthService to handle timing during page reload.
 */
export const guestGuard: CanActivateFn = (_route, _state) => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check both AuthStore AND AuthService for authentication
  const isStoreAuthenticated = authStore.isLoggedIn();
  const isServiceAuthenticated = authService.isAuthenticated();

  if (!isStoreAuthenticated && !isServiceAuthenticated) {
    return true;
  } else {
    // Redirect to dashboard if already authenticated
    router.navigate(['/dashboard']);
    return false;
  }
};
